import {
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
  useLayoutEffect,
} from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import ConfirmationFilterChips from "../../components/calendar/ConfirmationFilterChips";
import ContentLoader from "../../components/shared/ContentLoader";
import {
  countConfirmationStates,
  canSendManualReminder,
} from "../../utils/appointmentDisplay";
import {
  useVisibilityRefresh,
  useCombinedRefetch,
  DEFAULT_POLLING_INTERVAL,
} from "../../hooks/useRealtimeQuery";
import {
  selectClinicId,
  selectClinicProfile,
  selectUser,
  selectTherapistId,
  selectTherapistName,
  selectCanAccessFinance,
  selectIsSecretary,
  selectIsAdmin,
  selectPreventTherapistDoubleBooking,
  selectBlockBookingOnAbsence,
} from "../../store/slices/authSlice";
import { toLocalDateStr, toIsraelHHMM, findBlockingAbsence } from "../../utils/absenceUtils";
import { useToast } from "../../contexts/ToastContext";
import {
  selectCurrentDate,
  selectViewMode,
  selectIsModalOpen,
  selectIsSettingsOpen,
  selectSelectedAppointment,
  setViewMode,
  setCurrentDate,
  goToToday,
  goToPrevious,
  goToNext,
  openModal,
  closeModal,
  openSettings,
  closeSettings,
  setRoomFilter,
  setShowRoomAvailability,
  selectRoomFilterId,
  selectShowRoomAvailability,
} from "../../store/slices/calendarSlice";
import {
  useGetAppointmentsQuery,
  useGetAvailabilityForRangeQuery,
  useGetAbsencesQuery,
  useUpdateAppointmentMutation,
  useUpdateRecurringAppointmentsMutation,
  useDeleteAppointmentMutation,
  useSendReminderNowMutation,
  calendarApi,
} from "../../store/services/calendarApi";
import {
  useUpdateTransactionMutation,
  financeApi,
} from "../../store/services/financeApi";
import { useGetPatientsQuery, PATIENTS_PICKER_PAGE_SIZE } from "../../store/services/patientsApi";
import {
  useGetRoomsQuery,
  useGetRoomResolvedAvailabilityForRangeQuery,
} from "../../store/services/roomsApi";
import {
  getTransactionByAppointment,
  checkRecurringConflicts,
  getAbsences,
  TRANSACTION_STATUS,
} from "../../firebase";
import AppointmentModal from "../../components/calendar/AppointmentEntryModal";
import BookingApprovalModal from "../../components/calendar/BookingApprovalModal";
import CalendarSettings from "../../components/calendar/CalendarSettings";
import CalendarPaymentModal from "../../components/calendar/CalendarPaymentModal";
import DragConfirmModal from "../../components/calendar/DragConfirmModal";
import DeleteAppointmentDialog from "../../components/calendar/DeleteAppointmentDialog";
import DayView from "../../components/calendar/DayView";
import WeekView from "../../components/calendar/WeekView";
import MonthView from "../../components/calendar/MonthView";
import CalendarSummaryOverlay from "../../components/calendar/CalendarSummaryOverlay";
import {
  CaretLeft,
  CaretRight,
  Plus,
  Gear,
  CalendarBlank,
  ArrowClockwise,
  CalendarCheck,
  NotePencil,
  CurrencyCircleDollar,
  CheckCircle,
  XCircle,
  PencilSimple,
  PaperPlaneTilt,
  Info,
  ProhibitInset,
  Hourglass,
  MinusCircle,
  FileText,
  Trash,
  Door,
} from "@phosphor-icons/react";
import { HelpButton, PageHeader } from "../../components/shared";
import { NoAccessState } from "../../components/shared/access";
import CustomClinikitActionButton from "../../components/ui/CustomClinikitActionButton";
import useCancelAppointmentFlow from "../../components/dashboard/hooks/useCancelAppointmentFlow";
import "./CalendarPage.css";

const MOBILE_BREAKPOINT = 768;

const VIEW_OPTIONS = [
  { value: "day", label: "יום" },
  { value: "week", label: "שבוע" },
  { value: "month", label: "חודש" },
];

function CalendarPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const clinicId = useSelector(selectClinicId);
  const therapistId = useSelector(selectTherapistId);
  const therapistName = useSelector(selectTherapistName);
  const clinicProfile = useSelector(selectClinicProfile);
  const user = useSelector(selectUser);
  const canAccessFinance = useSelector(selectCanAccessFinance);
  // Scheduling policies (multi-clinic v2) — same gate the wizard applies.
  // Absent policy ⇒ warn-only (anyone may force); block ⇒ admins only.
  const isAdmin = useSelector(selectIsAdmin);
  const preventTherapistDoubleBooking = useSelector(selectPreventTherapistDoubleBooking);
  const blockBookingOnAbsence = useSelector(selectBlockBookingOnAbsence);
  const canOverrideTherapistConflict = !preventTherapistDoubleBooking || isAdmin;
  const canOverrideAbsence = !blockBookingOnAbsence || isAdmin;
  const { showError: showDragError, showSuccess } = useToast();
  // Multi-clinic v2: secretaries have no personal calendar / availability.
  const isSecretary = useSelector(selectIsSecretary);
  const currentDate = useSelector(selectCurrentDate);
  const viewMode = useSelector(selectViewMode);
  const isModalOpen = useSelector(selectIsModalOpen);
  const isSettingsOpen = useSelector(selectIsSettingsOpen);
  const selectedAppointment = useSelector(selectSelectedAppointment);
  const roomFilterId = useSelector(selectRoomFilterId);
  const showRoomAvailability = useSelector(selectShowRoomAvailability);

  const [prefilledDate, setPrefilledDate] = useState(null);
  const [prefilledDateOnly, setPrefilledDateOnly] = useState(false);
  // Self-booked appointment awaiting therapist approval (opens its own modal)
  const [approvalAppointment, setApprovalAppointment] = useState(null);
  const [isMobile, setIsMobile] = useState(
    window.innerWidth < MOBILE_BREAKPOINT,
  );

  // Appointment right-click context menu state
  const [aptContextMenu, setAptContextMenu] = useState(null);
  const aptContextMenuRef = useRef(null);
  // Payment modal state
  const [paymentModalAppointment, setPaymentModalAppointment] = useState(null);
  // Approval loading state
  const [isApprovingAppointment, setIsApprovingAppointment] = useState(false);
  // Confirmation dialog for skip actions
  const [confirmDialog, setConfirmDialog] = useState(null);
  // Delete-appointment dialog state. The shared `DeleteAppointmentDialog`
  // component handles the linked-payment preload + UI internally — we only
  // need to know which appointment we're deleting and an in-flight flag.
  const [deleteDialog, setDeleteDialog] = useState({
    isOpen: false,
    appointment: null,
    isWorking: false,
  });
  // Drag & drop rescheduling state
  const [dragConfirmData, setDragConfirmData] = useState(null);
  const [isDragUpdating, setIsDragUpdating] = useState(false);
  // Series shift refused by the server — shown inside the drag dialog with an
  // explicit override.
  const [dragConflicts, setDragConflicts] = useState([]);
  const [dragAbsence, setDragAbsence] = useState(null); // blocking absence at the target slot

  // Mutations for appointment approval
  const [updateAppointment] = useUpdateAppointmentMutation();
  const [sendReminderNow] = useSendReminderNowMutation();
  const [updateRecurringAppointments] = useUpdateRecurringAppointmentsMutation();
  const [updateTransaction] = useUpdateTransactionMutation();
  const [deleteAppointment] = useDeleteAppointmentMutation();

  // Shared cancel-appointment flow (modal): reused from the AppointmentModal
  // and PendingAppointmentsCarousel so the cancellation UX is identical
  // regardless of the entry point (right-click context menu included).
  const { startCancel, startPostpone, cancelFlowNode } =
    useCancelAppointmentFlow();

  // Set default view to "day" on mobile
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < MOBILE_BREAKPOINT;
      setIsMobile(mobile);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Every entry to the calendar page starts on the weekly view, on the
  // current week (mobile and desktop alike).
  useEffect(() => {
    dispatch(setViewMode("week"));
    dispatch(goToToday());
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Calculate date range based on view mode
  const dateRange = useMemo(() => {
    const current = new Date(currentDate);
    let start, end;

    if (viewMode === "day") {
      start = new Date(
        current.getFullYear(),
        current.getMonth(),
        current.getDate(),
        0,
        0,
        0,
      );
      end = new Date(
        current.getFullYear(),
        current.getMonth(),
        current.getDate(),
        23,
        59,
        59,
      );
    } else if (viewMode === "week") {
      const dayOfWeek = current.getDay();
      start = new Date(current);
      start.setDate(current.getDate() - dayOfWeek);
      start.setHours(0, 0, 0, 0);
      end = new Date(start);
      end.setDate(start.getDate() + 6);
      end.setHours(23, 59, 59, 999);
    } else {
      start = new Date(current.getFullYear(), current.getMonth(), 1, 0, 0, 0);
      end = new Date(
        current.getFullYear(),
        current.getMonth() + 1,
        0,
        23,
        59,
        59,
      );
    }

    return { start, end };
  }, [currentDate, viewMode]);

  // Fetch appointments - filtered by therapistId (personal calendar) with real-time updates
  const {
    data: appointments = [],
    isLoading,
    refetch,
    isFetching,
  } = useGetAppointmentsQuery(
    {
      clinicId,
      therapistId, // Filter by current therapist - shows only their appointments
      startDate: dateRange.start.toISOString(),
      endDate: dateRange.end.toISOString(),
    },
    {
      skip: !clinicId || !therapistId,
      pollingInterval: DEFAULT_POLLING_INTERVAL,
    },
  );

  // ===== Room filter + availability overlay (clinics with rooms only) =====
  const { data: clinicRooms = [] } = useGetRoomsQuery(
    { clinicId },
    { skip: !clinicId }
  );
  const activeRooms = useMemo(
    () => clinicRooms.filter((r) => r.isActive !== false),
    [clinicRooms]
  );
  const filterRoom = roomFilterId
    ? activeRooms.find((r) => r.id === roomFilterId) || null
    : null;

  const { data: roomAvailabilityDays = [] } =
    useGetRoomResolvedAvailabilityForRangeQuery(
      {
        clinicId,
        roomId: roomFilterId,
        startDate: dateRange.start.toISOString(),
        endDate: dateRange.end.toISOString(),
      },
      {
        skip:
          !clinicId || !roomFilterId || !showRoomAvailability ||
          viewMode === "month",
      }
    );

  const displayedAppointments = useMemo(() => {
    if (!roomFilterId) return appointments;
    return appointments.filter((apt) => apt.roomId === roomFilterId);
  }, [appointments, roomFilterId]);

  // Quick confirmed / not-confirmed view (day + week). Local, not persisted —
  // dims non-matching cards in the views; counts follow the visible range.
  const [confirmationFilter, setConfirmationFilter] = useState("all");
  const confirmationCounts = useMemo(
    () => countConfirmationStates(displayedAppointments),
    [displayedAppointments]
  );

  const roomOverlay =
    roomFilterId && showRoomAvailability && viewMode !== "month"
      ? roomAvailabilityDays
      : null;

  // Calculate date range for upcoming appointments (next 30 days, excluding today)
  const upcomingRange = useMemo(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);

    const end = new Date(tomorrow);
    end.setDate(end.getDate() + 30);
    end.setHours(23, 59, 59, 999);

    return { start: tomorrow, end };
  }, []);

  // Fetch upcoming appointments (for sidebar) - filtered by therapistId with real-time updates
  const { data: upcomingAppointments = [], refetch: refetchUpcoming } =
    useGetAppointmentsQuery(
      {
        clinicId,
        therapistId, // Filter by current therapist
        startDate: upcomingRange.start.toISOString(),
        endDate: upcomingRange.end.toISOString(),
      },
      {
        skip: !clinicId || !therapistId,
        pollingInterval: DEFAULT_POLLING_INTERVAL,
      },
    );

  // Combine refetch functions for visibility refresh
  const combinedRefetch = useCombinedRefetch([refetch, refetchUpcoming]);

  // Auto-refresh when tab becomes visible
  useVisibilityRefresh(combinedRefetch);

  const handleRefresh = () => {
    refetch();
    refetchUpcoming();
  };

  // Fetch availability slots
  const { data: availabilitySlots = [] } = useGetAvailabilityForRangeQuery(
    {
      therapistId: user?.uid,
      startDate: dateRange.start.toISOString(),
      endDate: dateRange.end.toISOString(),
    },
    { skip: !user?.uid },
  );

  // Own absences in range (multi-clinic v2) — the therapist always sees
  // their own type/note.
  const { data: ownAbsencesRaw = [] } = useGetAbsencesQuery(
    {
      clinicId,
      therapistId: user?.uid,
      from: toLocalDateStr(dateRange.start),
      to: toLocalDateStr(dateRange.end),
    },
    { skip: !clinicId || !user?.uid },
  );
  const ownAbsences = useMemo(
    () => ownAbsencesRaw.map((a) => ({ ...a, canSeeDetails: true })),
    [ownAbsencesRaw],
  );

  // Fetch patients for the modal dropdown - only current therapist's patients
  const { data: patientsData } = useGetPatientsQuery(
    { clinicId, therapistId, pageSize: PATIENTS_PICKER_PAGE_SIZE },
    { skip: !clinicId || !therapistId },
  );

  const handleViewChange = (mode) => {
    dispatch(setViewMode(mode));
  };

  // ==========================================
  // APPOINTMENT CONTEXT MENU (right-click on appointments)
  // ==========================================

  const closeAptContextMenu = useCallback(() => setAptContextMenu(null), []);

  useEffect(() => {
    if (!aptContextMenu) return;
    const handleClick = (e) => {
      if (
        aptContextMenuRef.current &&
        !aptContextMenuRef.current.contains(e.target)
      ) {
        closeAptContextMenu();
      }
    };
    const handleKey = (e) => {
      if (e.key === "Escape") closeAptContextMenu();
    };

    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    document.addEventListener("scroll", closeAptContextMenu, true);

    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
      document.removeEventListener("scroll", closeAptContextMenu, true);
    };
  }, [aptContextMenu, closeAptContextMenu]);

  useLayoutEffect(() => {
    if (!aptContextMenu || !aptContextMenuRef.current) return;
    const rect = aptContextMenuRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let x = aptContextMenu.x;
    let y = aptContextMenu.y;
    if (x + rect.width > vw - 8) x = vw - rect.width - 8;
    if (y + rect.height > vh - 8) y = vh - rect.height - 8;
    aptContextMenuRef.current.style.left = `${x}px`;
    aptContextMenuRef.current.style.top = `${y}px`;
  }, [aptContextMenu]);

  const isAppointmentEnded = (appointment) => {
    if (!appointment) return false;
    if (appointment.status === "cancelled") return false;
    if (appointment.status === "completed") return true;
    if (!appointment.endTime) return false;
    const endMs = new Date(appointment.endTime).getTime();
    if (Number.isNaN(endMs)) return false;
    return endMs < Date.now();
  };

  const invalidateAllFinanceTags = (patientId = null) => {
    dispatch(
      calendarApi.util.invalidateTags([
        { type: "Finance", id: "STATS" },
        { type: "Finance", id: "PENDING_PAYMENTS" },
        { type: "Finance", id: "UPCOMING" },
        ...(patientId ? [{ type: "Finance", id: `PATIENT_${patientId}` }] : []),
      ]),
    );
    dispatch(
      financeApi.util.invalidateTags([
        { type: "TransactionStats", id: "MONTHLY" },
        { type: "PendingCollection", id: "LIST" },
        { type: "Transactions", id: "EXPECTED" },
        { type: "Transactions", id: "PAID_HISTORY" },
        ...(patientId
          ? [{ type: "Transactions", id: `PATIENT_${patientId}` }]
          : []),
      ]),
    );
  };

  /**
   * Context menu state machine:
   * - "not_ended": appointment hasn't ended → show "edit appointment"
   * - "needs_approval": ended but not approved → show "approve" only
   * - "has_pending": ended + approved + has pending steps → show pending steps
   * - "all_done": ended + all 3 steps done → show info text
   */
  const getContextMenuState = (appointment) => {
    if (!isAppointmentEnded(appointment)) return "not_ended";

    const isApproved = appointment.isApprovedByTherapist === true;
    if (!isApproved) return "needs_approval";

    const summaryDone = appointment.isTherapistSummarizedTheMeeting === true;
    const paymentDone = appointment.isTherapistUpdatedPaymentStatus === true;

    const canAddSummary = !summaryDone;
    const canAddPayment = canAccessFinance && !paymentDone;

    if (canAddSummary || canAddPayment) return "has_pending";
    return "all_done";
  };

  const handleAppointmentContextMenu = (e, appointment) => {
    if (appointment.status === "cancelled") return;

    // Blocked time never enters the ended/approval lifecycle — edit/delete only.
    const menuState =
      appointment.isBlockedTime === true
        ? "blocked_time"
        : getContextMenuState(appointment);

    const canAddSummary = appointment.isTherapistSummarizedTheMeeting !== true;
    const canAddPayment =
      canAccessFinance && appointment.isTherapistUpdatedPaymentStatus !== true;
    // Manual "שלח תזכורת עכשיו": future, scheduled, patient not confirmed yet
    // (shared rule with the wizard — utils/appointmentDisplay).
    const canSendReminder =
      menuState === "not_ended" && canSendManualReminder(appointment);

    setAptContextMenu({
      x: e.clientX,
      y: e.clientY,
      appointment,
      menuState,
      canAddSummary,
      canAddPayment,
      canSendReminder,
    });
  };

  /**
   * Manual reminder (WhatsApp-first / SMS fallback) for a future unconfirmed
   * meeting. Opens the shared quick-action confirm dialog; the Cloud Function
   * (`sendAppointmentReminderNow`) owns eligibility, permission, 15-min
   * cooldown, quota and stamps. The scheduled automatic reminder is kept.
   *
   * Error Code: CK-3826
   */
  const handleSendReminderFromMenu = () => {
    const appointment = aptContextMenu?.appointment;
    closeAptContextMenu();
    if (!appointment) return;

    const start = new Date(appointment.startTime);
    const when = `${start.toLocaleDateString("he-IL", {
      weekday: "long",
      day: "numeric",
      month: "long",
    })} בשעה ${start.toLocaleTimeString("he-IL", {
      hour: "2-digit",
      minute: "2-digit",
    })}`;
    const autoReminderPending =
      appointment.reminderSmsEnabled !== false &&
      appointment.whatsappReminderSent !== true &&
      appointment.reminderSmsScheduledAt &&
      new Date(appointment.reminderSmsScheduledAt).getTime() > Date.now();

    setConfirmDialog({
      title: "שליחת תזכורת למטופל",
      description:
        `תישלח עכשיו תזכורת ל${appointment.patientName} לפגישה ב${when}, ` +
        "עם קישור לאישור הגעה. ההודעה תישלח בוואטסאפ, או ב-SMS אם אין וואטסאפ." +
        (autoReminderPending
          ? " התזכורת האוטומטית המתוזמנת תישלח כרגיל."
          : ""),
      confirmLabel: "שלח תזכורת",
      appointment,
      onConfirm: async () => {
        setConfirmDialog((prev) =>
          prev ? { ...prev, isLoading: true, error: null } : null,
        );
        try {
          const result = await sendReminderNow({
            clinicId,
            appointmentId: appointment.id,
            therapistId,
            therapistName,
            patientName: appointment.patientName,
          }).unwrap();
          setConfirmDialog(null);
          showSuccess(
            result?.channel === "whatsapp"
              ? "התזכורת נשלחה למטופל/ת בוואטסאפ"
              : "התזכורת נשלחה למטופל/ת ב-SMS",
          );
        } catch (error) {
          const message =
            error?.message || error?.data?.message || "שליחת התזכורת נכשלה";
          console.error("[CK-3826] Error sending manual reminder from calendar:", {
            errorCode: "CK-3826",
            errorMessage: message,
            appointmentId: appointment.id,
            timestamp: new Date().toISOString(),
          });
          setConfirmDialog((prev) =>
            prev ? { ...prev, isLoading: false, error: message } : null,
          );
        }
      },
    });
  };

  const handleApproveAppointment = async () => {
    const appointment = aptContextMenu?.appointment;
    closeAptContextMenu();
    if (!appointment) return;

    setIsApprovingAppointment(true);
    try {
      await updateAppointment({
        clinicId,
        appointmentId: appointment.id,
        appointmentData: {
          isApprovedByTherapist: true,
          NotifyAboutThisMeetingApproval: false,
        },
        therapistId,
        therapistName,
        patientName: appointment.patientName,
      }).unwrap();

      try {
        const transaction = await getTransactionByAppointment(
          clinicId,
          appointment.id,
        );
        if (transaction) {
          await updateTransaction({
            clinicId,
            transactionId: transaction.id,
            updateData: {
              status: TRANSACTION_STATUS.PENDING_COLLECTION,
            },
            therapistId,
            therapistName,
            patientName: appointment.patientName,
            patientId: appointment.patientId,
          }).unwrap();

          invalidateAllFinanceTags(appointment.patientId);
        }
      } catch (txError) {
        console.error(
          "[CK-3510] Error updating transaction to pending_collection:",
          {
            errorCode: "CK-3510",
            errorMessage: txError.message,
            appointmentId: appointment.id,
            timestamp: new Date().toISOString(),
          },
        );
      }
    } catch (error) {
      console.error("[CK-3511] Error approving appointment from calendar:", {
        errorCode: "CK-3511",
        errorMessage: error.message,
        appointmentId: appointment.id,
        timestamp: new Date().toISOString(),
      });
    } finally {
      setIsApprovingAppointment(false);
    }
  };

  const handleCancelAppointment = () => {
    const appointment = aptContextMenu?.appointment;
    closeAptContextMenu();
    if (!appointment) return;
    startCancel(appointment);
  };

  /**
   * Open the shared delete dialog from the context menu. The dialog itself
   * handles loading the linked transaction, asking the therapist whether to
   * also remove the payment record, and warning about external invoices.
   *
   * Recurring appointments are intentionally redirected to the appointment
   * modal so the therapist can choose the scope (single / future / all) —
   * the calendar context menu is reserved for one-shot quick actions.
   *
   * Error Code: CK-3514
   */
  const handleDeleteAppointment = () => {
    const appointment = aptContextMenu?.appointment;
    closeAptContextMenu();
    if (!appointment) return;

    if (appointment.isRecurring) {
      dispatch(openModal(appointment));
      return;
    }

    setDeleteDialog({
      isOpen: true,
      appointment,
      isWorking: false,
    });
  };

  /**
   * Mutation handler for the shared delete dialog. Receives the therapist's
   * payment-tracking choice and forwards it to the RTK mutation.
   *
   * Error Code: CK-3514
   */
  const handleDeleteAppointmentConfirm = async ({ deleteLinkedTransaction }) => {
    const appointment = deleteDialog.appointment;
    if (!appointment) return;
    setDeleteDialog((prev) => ({ ...prev, isWorking: true }));
    try {
      await deleteAppointment({
        clinicId,
        appointmentId: appointment.id,
        patientName: appointment.patientName,
        therapistId,
        therapistName,
        deleteLinkedTransaction: !!deleteLinkedTransaction,
      }).unwrap();
      invalidateAllFinanceTags(appointment.patientId);
      setDeleteDialog({ isOpen: false, appointment: null, isWorking: false });
    } catch (error) {
      console.error("[CK-3514] Error soft-deleting appointment from calendar:", {
        errorCode: "CK-3514",
        errorMessage: error.message,
        appointmentId: appointment.id,
        timestamp: new Date().toISOString(),
      });
      setDeleteDialog((prev) => ({ ...prev, isWorking: false }));
    }
  };

  const handleEditAppointmentFromMenu = () => {
    const appointment = aptContextMenu?.appointment;
    closeAptContextMenu();
    if (!appointment) return;
    if (appointment.status === "pending_approval") {
      setApprovalAppointment(appointment);
      return;
    }
    dispatch(openModal(appointment));
  };

  const handleAddTreatmentSummary = () => {
    const appointment = aptContextMenu?.appointment;
    closeAptContextMenu();
    if (!appointment) return;
    navigate(
      `/dashboard/patients/${appointment.patientId}/treatments/new?appointmentId=${appointment.id}`,
    );
  };

  const handleAddPaymentDoc = () => {
    const appointment = aptContextMenu?.appointment;
    closeAptContextMenu();
    if (!appointment) return;
    setPaymentModalAppointment(appointment);
  };

  const handleViewTreatmentSummary = () => {
    const appointment = aptContextMenu?.appointment;
    closeAptContextMenu();
    if (!appointment || !appointment.treatmentId) return;
    navigate(
      `/dashboard/patients/${appointment.patientId}/treatments/${appointment.treatmentId}`,
    );
  };

  const handleSkipSummary = () => {
    const appointment = aptContextMenu?.appointment;
    closeAptContextMenu();
    if (!appointment) return;

    setConfirmDialog({
      title: "דילוג על סיכום טיפול",
      description: `הפגישה עם ${appointment.patientName} תסומן כפגישה שלא נדרש עבורה סיכום טיפול. שלב זה ייסגר ולא יופיע יותר בפגישות ממתינות.`,
      appointment,
      onConfirm: async () => {
        setConfirmDialog((prev) =>
          prev ? { ...prev, isLoading: true } : null,
        );
        try {
          await updateAppointment({
            clinicId,
            appointmentId: appointment.id,
            appointmentData: {
              isTherapistSummarizedTheMeeting: true,
              NotifyAboutThisMeetingSummarize: false,
            },
            therapistId,
            therapistName,
            patientName: appointment.patientName,
          }).unwrap();
          setConfirmDialog(null);
        } catch (error) {
          console.error("[CK-3520] Error skipping summary:", {
            errorCode: "CK-3520",
            errorMessage: error.message,
            appointmentId: appointment.id,
            timestamp: new Date().toISOString(),
          });
          setConfirmDialog((prev) =>
            prev
              ? { ...prev, isLoading: false, error: "שגיאה בעדכון. נסה שוב." }
              : null,
          );
        }
      },
    });
  };

  const handleMarkPendingCollection = () => {
    const appointment = aptContextMenu?.appointment;
    closeAptContextMenu();
    if (!appointment) return;

    setConfirmDialog({
      title: 'סימון כ"ממתין לגבייה"',
      description: `התשלום עבור הפגישה עם ${appointment.patientName} יסומן בסטטוס "ממתין לגבייה". הפגישה תופיע ברשימת החובות ותוכל לעקוב אחרי הגבייה בעמוד הפיננסים.`,
      appointment,
      onConfirm: async () => {
        setConfirmDialog((prev) =>
          prev ? { ...prev, isLoading: true } : null,
        );
        try {
          await updateAppointment({
            clinicId,
            appointmentId: appointment.id,
            appointmentData: {
              isTherapistUpdatedPaymentStatus: true,
              NotifyAboutThisMeetingPaymentStatus: false,
              paymentStatus: "debt",
            },
            therapistId,
            therapistName,
            patientName: appointment.patientName,
          }).unwrap();

          try {
            const transaction = await getTransactionByAppointment(
              clinicId,
              appointment.id,
            );
            if (transaction) {
              await updateTransaction({
                clinicId,
                transactionId: transaction.id,
                updateData: { status: TRANSACTION_STATUS.PENDING_COLLECTION },
                therapistId,
                therapistName,
                patientName: appointment.patientName,
                patientId: appointment.patientId,
              }).unwrap();
              invalidateAllFinanceTags(appointment.patientId);
            }
          } catch (txError) {
            console.error("[CK-3521a] Error updating transaction:", {
              errorCode: "CK-3521a",
              errorMessage: txError.message,
              appointmentId: appointment.id,
              timestamp: new Date().toISOString(),
            });
          }

          setConfirmDialog(null);
        } catch (error) {
          console.error("[CK-3521] Error marking as pending collection:", {
            errorCode: "CK-3521",
            errorMessage: error.message,
            appointmentId: appointment.id,
            timestamp: new Date().toISOString(),
          });
          setConfirmDialog((prev) =>
            prev
              ? { ...prev, isLoading: false, error: "שגיאה בעדכון. נסה שוב." }
              : null,
          );
        }
      },
    });
  };

  const handleSkipPayment = () => {
    const appointment = aptContextMenu?.appointment;
    closeAptContextMenu();
    if (!appointment) return;

    setConfirmDialog({
      title: "דילוג על גביית תשלום",
      description: `הפגישה עם ${appointment.patientName} תסומן כפגישה שאין צורך לגבות עבורה תשלום. שלב זה ייסגר ולא יופיע יותר בפגישות ממתינות.`,
      appointment,
      onConfirm: async () => {
        setConfirmDialog((prev) =>
          prev ? { ...prev, isLoading: true } : null,
        );
        try {
          await updateAppointment({
            clinicId,
            appointmentId: appointment.id,
            appointmentData: {
              isTherapistUpdatedPaymentStatus: true,
              NotifyAboutThisMeetingPaymentStatus: false,
              paymentStatus: "skipped",
            },
            therapistId,
            therapistName,
            patientName: appointment.patientName,
          }).unwrap();
          setConfirmDialog(null);
        } catch (error) {
          console.error("[CK-3522] Error skipping payment:", {
            errorCode: "CK-3522",
            errorMessage: error.message,
            appointmentId: appointment.id,
            timestamp: new Date().toISOString(),
          });
          setConfirmDialog((prev) =>
            prev
              ? { ...prev, isLoading: false, error: "שגיאה בעדכון. נסה שוב." }
              : null,
          );
        }
      },
    });
  };

  // ==========================================
  // DRAG & DROP RESCHEDULING
  // ==========================================
  const handleDragDrop = (
    appointment,
    targetDate,
    targetHour,
    targetMinute,
  ) => {
    const originalStart = new Date(appointment.startTime);
    const originalEnd = new Date(appointment.endTime);
    const durationMs = originalEnd - originalStart;

    const newStart = new Date(targetDate);
    newStart.setHours(targetHour, targetMinute, 0, 0);
    const newEnd = new Date(newStart.getTime() + durationMs);

    if (originalStart.getTime() === newStart.getTime()) return;

    setDragConflicts([]);
    setDragAbsence(null);
    setDragConfirmData({
      appointment,
      newStartTime: newStart,
      newEndTime: newEnd,
    });
  };

  const handleDragConfirm = async ({
    sendSms,
    startTime,
    endTime,
    scope = "single",
    allowConflicts = false,
  }) => {
    if (!dragConfirmData) return;
    const { appointment, newStartTime, newEndTime } = dragConfirmData;
    const finalStart = startTime || newStartTime;
    const finalEnd = endTime || newEndTime;
    const targetTherapistId = appointment.therapistId || therapistId;
    // A "force" re-submit is only honoured when the clinic policy lets this
    // user waive (client gate; the dialog hides the button otherwise).
    // Each gate is waived only for what the dialog actually showed: forcing
    // past an absence must not skip a conflict check that never ran.
    const forceConflicts = allowConflicts && canOverrideTherapistConflict && dragConflicts.length > 0;
    const forceAbsence = allowConflicts && canOverrideAbsence && !!dragAbsence;
    setIsDragUpdating(true);
    try {
      // Absence gate (same check the wizard runs via useAppointmentChecks).
      // Series scopes check the dropped occurrence only, like the wizard.
      const dateStr = toLocalDateStr(finalStart);
      const hhmm = toIsraelHHMM(finalStart);
      const durationMin = Math.max(1, Math.round((finalEnd - finalStart) / 60000));
      const absences = await getAbsences(clinicId, { from: dateStr, to: dateStr, therapistId: targetTherapistId });
      const blockingAbsence = findBlockingAbsence(absences, dateStr, hhmm, durationMin);
      if (blockingAbsence && !forceAbsence) {
        setDragAbsence(blockingAbsence);
        return;
      }

      const appointmentData = {
        startTime: finalStart.toISOString(),
        endTime: finalEnd.toISOString(),
        // Slot changed: an old waiver no longer describes this slot. Re-stamp
        // only when the user actually forced past a conflict/absence now.
        overrideTherapistConflict: false,
        therapistConflictOverride: null,
        overrideAbsence: false,
        absenceOverride: null,
      };
      if (blockingAbsence && forceAbsence) {
        appointmentData.overrideAbsence = true;
        appointmentData.absenceOverride = {
          by: therapistId,
          at: new Date().toISOString(),
          targetTherapistId,
          absenceId: blockingAbsence.id,
          policy: blockBookingOnAbsence ? "block" : "warn",
          via: "drag",
        };
      }
      if (sendSms && !appointment.reminderSmsEnabled) {
        appointmentData.reminderSmsEnabled = true;
      } else if (!sendSms && appointment.reminderSmsEnabled) {
        appointmentData.reminderSmsEnabled = false;
      }

      // Series scope chosen in the dialog: move the later occurrences too, or
      // push them out by a week. "single" keeps the plain single-meeting write.
      if (appointment.isRecurring && scope !== "single") {
        if (forceConflicts && dragConflicts.length > 0) {
          appointmentData.overrideTherapistConflict = true;
          appointmentData.therapistConflictOverride = {
            by: therapistId,
            at: new Date().toISOString(),
            targetTherapistId,
            conflictIds: dragConflicts.map((c) => c.existingAppointment?.id).filter(Boolean),
            policy: preventTherapistDoubleBooking ? "block" : "warn",
            via: "drag",
          };
        }
        const result = await updateRecurringAppointments({
          clinicId,
          appointmentId: appointment.id,
          updateData: appointmentData,
          scope,
          patientName: appointment.patientName,
          therapistId,
          therapistName,
          allowConflicts: forceConflicts,
        }).unwrap();

        // Refused — nothing was written. Keep the dialog open with the list.
        if (result?.hasConflicts) {
          setDragConflicts(result.conflicts || []);
          return;
        }
      } else {
        // Single move: same overlap gate the wizard applies. Without it a drop
        // onto an occupied slot double-booked the therapist silently; series
        // scopes already refuse via findSeriesShiftConflicts. The dialog's
        // "force" button re-submits with allowConflicts=true.
        if (!forceConflicts) {
          const check = await checkRecurringConflicts(
            clinicId,
            targetTherapistId,
            [{ startTime: finalStart, endTime: finalEnd }],
            [appointment.id]
          );
          if (check?.hasConflicts) {
            setDragConflicts(check.conflicts || []);
            return;
          }
        } else if (dragConflicts.length > 0) {
          appointmentData.overrideTherapistConflict = true;
          appointmentData.therapistConflictOverride = {
            by: therapistId,
            at: new Date().toISOString(),
            targetTherapistId,
            conflictIds: dragConflicts.map((c) => c.existingAppointment?.id).filter(Boolean),
            policy: preventTherapistDoubleBooking ? "block" : "warn",
            via: "drag",
          };
        }

        await updateAppointment({
          clinicId,
          appointmentId: appointment.id,
          appointmentData,
          therapistId,
          therapistName,
          patientName: appointment.patientName,
        }).unwrap();
      }

      setDragConflicts([]);
      setDragAbsence(null);
      setDragConfirmData(null);
    } catch (error) {
      console.error("[CK-3531] Error rescheduling appointment via drag:", {
        errorCode: appointment.isRecurring && scope !== "single" ? "CK-3531" : "CK-3530",
        errorMessage: error.message,
        appointmentId: appointment.id,
        scope,
        timestamp: new Date().toISOString(),
      });
      showDragError("שגיאה בהעברת התור. נסו שוב.");
    } finally {
      setIsDragUpdating(false);
    }
  };

  const handleDragCancel = () => {
    setDragConfirmData(null);
    setDragConflicts([]);
    setDragAbsence(null);
  };

  const handleAddAppointment = (date = null, dateOnly = false) => {
    setPrefilledDate(date);
    setPrefilledDateOnly(dateOnly);
    dispatch(openModal());
  };

  const handleEditAppointment = (appointment) => {
    // Pending self-booking requests get the approval modal, not the editor.
    if (appointment?.status === "pending_approval") {
      setApprovalAppointment(appointment);
      return;
    }
    dispatch(openModal(appointment));
  };

  const handleAddTaskForDate = (selectedDate) => {
    const year = selectedDate.getFullYear();
    const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
    const day = String(selectedDate.getDate()).padStart(2, "0");
    navigate(
      `/dashboard/tasks?tab=tasks&action=new&date=${year}-${month}-${day}`,
    );
  };

  const handleCloseModal = () => {
    setPrefilledDate(null);
    setPrefilledDateOnly(false);
    dispatch(closeModal());
  };

  const formatHeaderDate = () => {
    const current = new Date(currentDate);
    const options = { month: "long", year: "numeric" };

    if (viewMode === "day") {
      return current.toLocaleDateString("he-IL", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    }

    if (viewMode === "week") {
      const weekStart = dateRange.start;
      const weekEnd = dateRange.end;

      // Check if week spans two different months
      if (weekStart.getMonth() === weekEnd.getMonth()) {
        // Same month: "10-16 בינואר 2026"
        return `${weekStart.getDate()}-${weekEnd.getDate()} ${weekStart.toLocaleDateString(
          "he-IL",
          { month: "long", year: "numeric" },
        )}`;
      } else {
        // Different months: "28 בדצמבר - 3 בינואר 2026"
        const startStr = weekStart.toLocaleDateString("he-IL", {
          day: "numeric",
          month: "long",
        });
        const endStr = weekEnd.toLocaleDateString("he-IL", {
          day: "numeric",
          month: "long",
          year: "numeric",
        });
        return `${startStr} - ${endStr}`;
      }
    }

    return current.toLocaleDateString("he-IL", options);
  };

  // Get today's appointments for summary
  const todayAppointments = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    return appointments.filter((apt) => {
      const aptDate = new Date(apt.startTime);
      return aptDate >= today && aptDate < tomorrow;
    });
  }, [appointments]);

  // Secretaries have no personal calendar — the team calendar lives at
  // /dashboard/clinic-calendar. Rendered after all hooks above.
  if (isSecretary) {
    return (
      <div className="calendar-page">
        <PageHeader icon={CalendarCheck} title="יומן ותורים" />
        <NoAccessState
          message="למזכירות אין יומן אישי. היומן של הצוות נמצא ב'יומן הקליניקה'."
          size="page"
        />
      </div>
    );
  }

  return (
    <div className={`calendar-page ${isMobile ? "mobile" : ""}`}>
      {/* Page Title Row */}
      <PageHeader
        icon={CalendarCheck}
        title="יומן ותורים"
        actions={<HelpButton featureKey="calendar" />}
      />

      {/* Main Content */}
      <div className="calendar-content">
        <div className="calendar-main">
          {/* Integrated toolbar — part of the calendar card, above the days */}
          <div className="calendar-toolbar">
            <div className="ct-nav">
              <div className="ct-nav-group">
                <button
                  className="ct-nav-btn"
                  onClick={() => dispatch(goToPrevious())}
                  aria-label="תקופה קודמת"
                >
                  <CaretRight size={16} weight="bold" />
                </button>
                <h2 className="ct-date">{formatHeaderDate()}</h2>
                <button
                  className="ct-nav-btn"
                  onClick={() => dispatch(goToNext())}
                  aria-label="תקופה הבאה"
                >
                  <CaretLeft size={16} weight="bold" />
                </button>
              </div>
              <button
                className="ct-today-btn"
                onClick={() => dispatch(goToToday())}
              >
                <CalendarBlank size={14} weight="regular" />
                <span>הצג היום</span>
              </button>
            </div>

            <div className="ct-actions">
              <div className="ct-view-toggle">
                {VIEW_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    className={`ct-view-btn ${
                      viewMode === option.value ? "active" : ""
                    }`}
                    onClick={() => handleViewChange(option.value)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>

              {activeRooms.length > 0 && (
                <div className="ct-room-filter">
                  <select
                    className="ct-room-filter-select"
                    value={roomFilterId || ""}
                    onChange={(e) => dispatch(setRoomFilter(e.target.value || null))}
                    title="סינון לפי חדר"
                  >
                    <option value="">כל החדרים</option>
                    {activeRooms.map((room) => (
                      <option key={room.id} value={room.id}>
                        {room.name}
                      </option>
                    ))}
                  </select>
                  {filterRoom && viewMode !== "month" && (
                    <button
                      className={`ct-icon-btn ${showRoomAvailability ? "active" : ""}`}
                      onClick={() =>
                        dispatch(setShowRoomAvailability(!showRoomAvailability))
                      }
                      title={
                        showRoomAvailability
                          ? "הסתרת שעות הזמינות של החדר"
                          : "הצגת שעות הזמינות של החדר"
                      }
                    >
                      <Door size={17} weight={showRoomAvailability ? "fill" : "regular"} />
                    </button>
                  )}
                </div>
              )}

              <button
                className="ct-icon-btn"
                onClick={handleRefresh}
                disabled={isFetching}
                title="רענן נתונים"
              >
                <ArrowClockwise
                  size={17}
                  weight="bold"
                  className={isFetching ? "spinning" : ""}
                />
              </button>

              {!isSecretary && (
                <button
                  className="ct-icon-btn"
                  onClick={() => dispatch(openSettings())}
                  title="הגדרות יומן"
                >
                  <Gear size={18} weight="regular" />
                </button>
              )}

              <CustomClinikitActionButton
                className="calendar-create-btn"
                icon={<Plus size={15} weight="bold" />}
                onClick={() =>
                  viewMode === "day"
                    ? handleAddAppointment(new Date(currentDate), true)
                    : handleAddAppointment()
                }
              >
                פגישה חדשה
              </CustomClinikitActionButton>
            </div>
          </div>

          {viewMode !== "month" && (
            <div className="calendar-toolbar-filters">
              <ConfirmationFilterChips
                value={confirmationFilter}
                onChange={setConfirmationFilter}
                counts={confirmationCounts}
              />
            </div>
          )}

          {isLoading ? (
            <ContentLoader fill message="טוען יומן..." />
          ) : (
            <>
              {viewMode === "day" && (
                <DayView
                  date={new Date(currentDate)}
                  appointments={displayedAppointments}
                  roomAvailability={roomOverlay}
                  availabilitySlots={availabilitySlots}
                  absences={ownAbsences}
                  onSlotClick={handleAddAppointment}
                  onAppointmentClick={handleEditAppointment}
                  onAppointmentContextMenu={handleAppointmentContextMenu}
                  onAddTask={handleAddTaskForDate}
                  onDragDrop={handleDragDrop}
                  confirmationFilter={confirmationFilter}
                />
              )}
              {viewMode === "week" && (
                <WeekView
                  startDate={dateRange.start}
                  appointments={displayedAppointments}
                  roomAvailability={roomOverlay}
                  availabilitySlots={availabilitySlots}
                  absences={ownAbsences}
                  onSlotClick={handleAddAppointment}
                  onAppointmentClick={handleEditAppointment}
                  onAppointmentContextMenu={handleAppointmentContextMenu}
                  onViewDay={(clickedDate) => {
                    dispatch(setCurrentDate(clickedDate.toISOString()));
                    dispatch(setViewMode("day"));
                  }}
                  onAddTask={handleAddTaskForDate}
                  onDragDrop={handleDragDrop}
                  confirmationFilter={confirmationFilter}
                />
              )}
              {viewMode === "month" && (
                <MonthView
                  date={new Date(currentDate)}
                  appointments={displayedAppointments}
                  availabilitySlots={availabilitySlots}
                  onDayClick={(clickedDate) => {
                    dispatch(setCurrentDate(clickedDate.toISOString()));
                    dispatch(setViewMode("day"));
                  }}
                  onAppointmentClick={handleEditAppointment}
                  onAppointmentContextMenu={handleAppointmentContextMenu}
                  onCreateAppointment={handleAddAppointment}
                  onAddTask={handleAddTaskForDate}
                />
              )}
            </>
          )}

          {/* Floating glass summary bar (today + next week + legend) */}
          <CalendarSummaryOverlay
            todayAppointments={todayAppointments}
            upcomingAppointments={upcomingAppointments}
            onAppointmentClick={handleEditAppointment}
          />
        </div>
      </div>

      {/* Appointment Modal */}
      {isModalOpen && (
        <AppointmentModal
          appointment={selectedAppointment}
          prefilledDate={prefilledDate}
          prefilledDateOnly={prefilledDateOnly}
          patients={patientsData?.patients || []}
          onClose={handleCloseModal}
        />
      )}

      {/* Booking Approval Modal (self-booked, pending therapist approval) */}
      {approvalAppointment && (
        <BookingApprovalModal
          appointment={approvalAppointment}
          patients={patientsData?.patients || []}
          onClose={() => setApprovalAppointment(null)}
          onDecided={() => setApprovalAppointment(null)}
        />
      )}

      {/* Settings Modal */}
      {isSettingsOpen && (
        <CalendarSettings
          clinicProfile={clinicProfile}
          onClose={() => dispatch(closeSettings())}
        />
      )}

      {/* Appointment Right-Click Context Menu */}
      {aptContextMenu && (
        <div
          ref={aptContextMenuRef}
          className="calendar-context-menu appointment-context-menu"
          style={{
            position: "fixed",
            top: aptContextMenu.y,
            left: aptContextMenu.x,
          }}
        >
          <div className="context-menu-date-header">
            {aptContextMenu.appointment.patientName}
            {" • "}
            {new Date(aptContextMenu.appointment.startTime).toLocaleDateString(
              "he-IL",
              { weekday: "short", day: "numeric", month: "short" },
            )}{" "}
            {new Date(aptContextMenu.appointment.startTime).toLocaleTimeString(
              "he-IL",
              { hour: "2-digit", minute: "2-digit" },
            )}
          </div>

          {/* Blocked time → edit or delete only */}
          {aptContextMenu.menuState === "blocked_time" && (
            <>
              <button
                className="context-menu-item"
                onClick={handleEditAppointmentFromMenu}
              >
                <PencilSimple size={18} weight="duotone" />
                <span>ערוך חסימת זמן</span>
              </button>
              <div className="context-menu-divider" />
              <button
                className="context-menu-item context-menu-item-danger"
                onClick={handleDeleteAppointment}
              >
                <Trash size={18} weight="duotone" />
                <span>מחק חסימה</span>
              </button>
            </>
          )}

          {/* State: Not ended → edit, cancel, or delete */}
          {aptContextMenu.menuState === "not_ended" && (
            <>
              <button
                className="context-menu-item"
                onClick={handleEditAppointmentFromMenu}
              >
                <PencilSimple size={18} weight="duotone" />
                <span>ערוך פרטי פגישה</span>
              </button>
              {aptContextMenu.canSendReminder && (
                <button
                  className="context-menu-item"
                  onClick={handleSendReminderFromMenu}
                >
                  <PaperPlaneTilt size={18} weight="duotone" />
                  <span>שלח תזכורת עכשיו</span>
                </button>
              )}
              <div className="context-menu-divider" />
              <button
                className="context-menu-item context-menu-item-warning"
                onClick={handleCancelAppointment}
              >
                <XCircle size={18} weight="duotone" />
                <span>בטל פגישה</span>
              </button>
              <button
                className="context-menu-item context-menu-item-danger"
                onClick={handleDeleteAppointment}
              >
                <Trash size={18} weight="duotone" />
                <span>מחק פגישה</span>
              </button>
            </>
          )}

          {/* State: Ended but not approved → approve or cancel */}
          {aptContextMenu.menuState === "needs_approval" && (
            <>
              <button
                className="context-menu-item"
                onClick={handleApproveAppointment}
                disabled={isApprovingAppointment}
              >
                <CheckCircle size={18} weight="duotone" />
                <span>
                  {isApprovingAppointment ? "מעדכן..." : "אשר שהפגישה התקיימה"}
                </span>
              </button>
              <div className="context-menu-divider" />
              <button
                className="context-menu-item context-menu-item-warning"
                onClick={handleCancelAppointment}
                disabled={isApprovingAppointment}
              >
                <XCircle size={18} weight="duotone" />
                <span>בטל פגישה זו</span>
              </button>
              <button
                className="context-menu-item context-menu-item-danger"
                onClick={handleDeleteAppointment}
                disabled={isApprovingAppointment}
              >
                <Trash size={18} weight="duotone" />
                <span>מחק פגישה</span>
              </button>
            </>
          )}

          {/* State: Approved + has pending steps → show relevant options */}
          {aptContextMenu.menuState === "has_pending" && (
            <>
              {aptContextMenu.canAddSummary && (
                <>
                  <button
                    className="context-menu-item"
                    onClick={handleAddTreatmentSummary}
                  >
                    <NotePencil size={18} weight="duotone" />
                    <span>הוסף תיעוד סיכום טיפול</span>
                  </button>
                  <button
                    className="context-menu-item context-menu-item-secondary"
                    onClick={handleSkipSummary}
                  >
                    <ProhibitInset size={18} weight="duotone" />
                    <span>לא נדרש סיכום טיפול</span>
                  </button>
                </>
              )}
              {aptContextMenu.canAddSummary && aptContextMenu.canAddPayment && (
                <div className="context-menu-divider" />
              )}
              {aptContextMenu.canAddPayment && (
                <>
                  <button
                    className="context-menu-item"
                    onClick={handleAddPaymentDoc}
                  >
                    <CurrencyCircleDollar size={18} weight="duotone" />
                    <span>הוסף תיעוד תשלום</span>
                  </button>
                  <button
                    className="context-menu-item context-menu-item-secondary"
                    onClick={handleMarkPendingCollection}
                  >
                    <Hourglass size={18} weight="duotone" />
                    <span>סמן כ"ממתין לגבייה"</span>
                  </button>
                  <button
                    className="context-menu-item context-menu-item-secondary"
                    onClick={handleSkipPayment}
                  >
                    <MinusCircle size={18} weight="duotone" />
                    <span>אין צורך לגבות תשלום</span>
                  </button>
                </>
              )}
              <div className="context-menu-divider" />
              <button
                className="context-menu-item context-menu-item-danger"
                onClick={handleDeleteAppointment}
              >
                <Trash size={18} weight="duotone" />
                <span>מחק פגישה</span>
              </button>
            </>
          )}

          {/* State: All done → info text + view treatment */}
          {aptContextMenu.menuState === "all_done" && (
            <>
              <div className="context-menu-info">
                <Info size={16} weight="fill" />
                <span>כל הסטטוסים עבור פגישה זו כבר עודכנו</span>
              </div>
              {aptContextMenu.appointment.treatmentId && (
                <>
                  <div className="context-menu-divider" />
                  <button
                    className="context-menu-item"
                    onClick={handleViewTreatmentSummary}
                  >
                    <FileText size={18} weight="duotone" />
                    <span>צפה בדו״ח סיכום טיפול</span>
                  </button>
                </>
              )}
              <div className="context-menu-divider" />
              <button
                className="context-menu-item context-menu-item-danger"
                onClick={handleDeleteAppointment}
              >
                <Trash size={18} weight="duotone" />
                <span>מחק פגישה</span>
              </button>
            </>
          )}
        </div>
      )}

      {/* Shared cancel-appointment flow modal (triggered by the context menu) */}
      {cancelFlowNode}

      {/* Calendar Payment Modal */}
      <CalendarPaymentModal
        isOpen={!!paymentModalAppointment}
        onClose={() => setPaymentModalAppointment(null)}
        appointment={paymentModalAppointment}
      />

      {/* Drag & Drop Reschedule Confirmation */}
      {dragConfirmData && (
        <DragConfirmModal
          appointment={dragConfirmData.appointment}
          newStartTime={dragConfirmData.newStartTime}
          newEndTime={dragConfirmData.newEndTime}
          onConfirm={handleDragConfirm}
          onCancel={handleDragCancel}
          isLoading={isDragUpdating}
          conflicts={dragConflicts}
          absence={dragAbsence}
          canForceConflicts={canOverrideTherapistConflict}
          canForceAbsence={canOverrideAbsence}
        />
      )}

      {/* Shared delete-appointment dialog (linked-payment + invoice aware) */}
      <DeleteAppointmentDialog
        isOpen={deleteDialog.isOpen}
        appointment={deleteDialog.appointment}
        clinicId={clinicId}
        isWorking={deleteDialog.isWorking}
        onClose={() => {
          if (deleteDialog.isWorking) return;
          setDeleteDialog({ isOpen: false, appointment: null, isWorking: false });
        }}
        onConfirm={handleDeleteAppointmentConfirm}
        onPostponeInstead={(appointment) => {
          setDeleteDialog({ isOpen: false, appointment: null, isWorking: false });
          startPostpone(appointment);
        }}
      />

      {/* Confirmation Dialog for skip/quick actions */}
      {confirmDialog && (
        <div
          className="confirm-dialog-overlay"
          onClick={() => !confirmDialog.isLoading && setConfirmDialog(null)}
        >
          <div className="confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-dialog-title">{confirmDialog.title}</div>
            <div className="confirm-dialog-description">
              {confirmDialog.description}
            </div>
            {confirmDialog.error && (
              <div className="confirm-dialog-error">{confirmDialog.error}</div>
            )}
            <div className="confirm-dialog-actionss">
              <button
                className={`confirm-dialog-btn ${
                  confirmDialog.variant === "danger"
                    ? "confirm-dialog-btn-danger"
                    : "confirm-dialog-btn-primary"
                }`}
                onClick={confirmDialog.onConfirm}
                disabled={confirmDialog.isLoading}
              >
                {confirmDialog.isLoading
                  ? "מעדכן..."
                  : confirmDialog.confirmLabel || "אישור"}
              </button>
              <button
                className="confirm-dialog-btn confirm-dialog-btn-cancel"
                onClick={() => setConfirmDialog(null)}
                disabled={confirmDialog.isLoading}
              >
                ביטול
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CalendarPage;
